export const SOCIAL_PLATFORMS = ['instagram', 'facebook', 'linkedin', 'twitter', 'youtube', 'tiktok'];

export const SOCIAL_PLATFORM_META = {
  instagram: {
    labelKey: 'company.settings.socialInstagram',
    placeholderKey: 'company.settings.socialInstagramPlaceholder',
    color: '#E4405F'
  },
  facebook: {
    labelKey: 'company.settings.socialFacebook',
    placeholderKey: 'company.settings.socialFacebookPlaceholder',
    color: '#1877F2'
  },
  linkedin: {
    labelKey: 'company.settings.socialLinkedin',
    placeholderKey: 'company.settings.socialLinkedinPlaceholder',
    color: '#0A66C2'
  },
  twitter: {
    labelKey: 'company.settings.socialTwitter',
    placeholderKey: 'company.settings.socialTwitterPlaceholder',
    color: '#000000'
  },
  youtube: {
    labelKey: 'company.settings.socialYoutube',
    placeholderKey: 'company.settings.socialYoutubePlaceholder',
    color: '#FF0000'
  },
  tiktok: {
    labelKey: 'company.settings.socialTiktok',
    placeholderKey: 'company.settings.socialTiktokPlaceholder',
    color: '#000000'
  }
};

const DEFAULT_SOCIAL_LINK = { enabled: false, url: '' };

export const DEFAULT_SOCIAL_MEDIA = SOCIAL_PLATFORMS.reduce((acc, platform) => {
  acc[platform] = { ...DEFAULT_SOCIAL_LINK };
  return acc;
}, {});

export const normalizeSocialUrl = (platform, value) => {
  const raw = String(value || '').trim();
  if (!raw) return '';

  if (/^https?:\/\//i.test(raw)) {
    return raw;
  }

  const handle = raw.replace(/^@/, '').replace(/^\//, '');
  if (!handle) return '';

  switch (platform) {
    case 'instagram':
      return `https://instagram.com/${handle}`;
    case 'facebook':
      return `https://facebook.com/${handle}`;
    case 'linkedin':
      return `https://linkedin.com/in/${handle}`;
    case 'twitter':
      return `https://x.com/${handle}`;
    case 'youtube':
      return handle.includes('/')
        ? `https://youtube.com/${handle}`
        : `https://youtube.com/@${handle}`;
    case 'tiktok':
      return `https://tiktok.com/@${handle.replace(/^@/, '')}`;
    default:
      return raw;
  }
};

export const normalizeSocialMedia = (socialMedia = {}) => {
  const normalized = {};

  SOCIAL_PLATFORMS.forEach((platform) => {
    const entry = socialMedia[platform] || {};
    const url = normalizeSocialUrl(platform, entry.url || '');
    normalized[platform] = {
      enabled: entry.enabled === true && Boolean(url),
      url
    };
  });

  return normalized;
};

export const SOCIAL_BUTTON_CLASS = {
  facebook: 'bg-[#1877F2]',
  twitter: 'bg-black',
  instagram: 'bg-gradient-to-br from-[#f9ce34] via-[#ee2a7b] to-[#6228d7]',
  linkedin: 'bg-[#0A66C2]',
  youtube: 'bg-[#FF0000]',
  tiktok: 'bg-black',
  whatsapp: 'bg-[#25D366]'
};

const FOOTER_SOCIAL_ORDER = ['facebook', 'twitter', 'instagram', 'linkedin', 'youtube', 'tiktok', 'whatsapp'];

export const getActiveSocialLinks = (socialMedia = {}) => (
  SOCIAL_PLATFORMS
    .map((platform) => {
      const entry = socialMedia[platform];
      if (!entry?.enabled || !entry?.url) return null;
      return {
        platform,
        url: entry.url,
        labelKey: SOCIAL_PLATFORM_META[platform]?.labelKey
      };
    })
    .filter(Boolean)
);

export const getFooterSocialLinks = (socialMedia = {}, { whatsappEnabled, whatsappPhone } = {}) => {
  const links = getActiveSocialLinks(socialMedia);
  const digits = String(whatsappPhone || '').replace(/\D/g, '');

  if (whatsappEnabled !== false && digits) {
    const hasWhatsApp = links.some((link) => link.platform === 'whatsapp');
    if (!hasWhatsApp) {
      links.push({
        platform: 'whatsapp',
        url: `https://wa.me/${digits}`,
        labelKey: 'storefront.footer.whatsappLine'
      });
    }
  }

  return links.sort(
    (a, b) => FOOTER_SOCIAL_ORDER.indexOf(a.platform) - FOOTER_SOCIAL_ORDER.indexOf(b.platform)
  );
};

export const SocialIcon = ({ platform, className = 'h-5 w-5' }) => {
  switch (platform) {
    case 'instagram':
      return (
        <svg viewBox="0 0 24 24" className={className} fill="currentColor" aria-hidden="true">
          <path d="M12 2.163c3.204 0 3.584.012 4.85.07 1.17.054 1.97.24 2.427.403a4.92 4.92 0 011.72 1.12 4.92 4.92 0 011.12 1.72c.163.457.349 1.257.403 2.427.058 1.266.07 1.646.07 4.85s-.012 3.584-.07 4.85c-.054 1.17-.24 1.97-.403 2.427a4.92 4.92 0 01-1.12 1.72 4.92 4.92 0 01-1.72 1.12c-.457.163-1.257.349-2.427.403-1.266.058-1.646.07-4.85.07s-3.584-.012-4.85-.07c-1.17-.054-1.97-.24-2.427-.403a4.92 4.92 0 01-1.72-1.12 4.92 4.92 0 01-1.12-1.72c-.163-.457-.349-1.257-.403-2.427C2.175 15.747 2.163 15.367 2.163 12s.012-3.584.07-4.85c.054-1.17.24-1.97.403-2.427a4.92 4.92 0 011.12-1.72 4.92 4.92 0 011.72-1.12c.457-.163 1.257-.349 2.427-.403C8.416 2.175 8.796 2.163 12 2.163zm0 1.622c-3.15 0-3.507.012-4.735.068-1.002.046-1.548.215-1.91.358a3.29 3.29 0 00-1.205.786 3.29 3.29 0 00-.786 1.205c-.143.362-.312.908-.358 1.91-.056 1.228-.068 1.585-.068 4.735s.012 3.507.068 4.735c.046 1.002.215 1.548.358 1.91.17.432.444.83.786 1.205.375.342.773.616 1.205.786.362.143.908.312 1.91.358 1.228.056 1.585.068 4.735.068s3.507-.012 4.735-.068c1.002-.046 1.548-.215 1.91-.358a3.29 3.29 0 001.205-.786 3.29 3.29 0 00.786-1.205c.143-.362.312-.908.358-1.91.056-1.228.068-1.585.068-4.735s-.012-3.507-.068-4.735c-.046-1.002-.215-1.548-.358-1.91a3.29 3.29 0 00-.786-1.205 3.29 3.29 0 00-1.205-.786c-.362-.143-.908-.312-1.91-.358-1.228-.056-1.585-.068-4.735-.068zM12 7.378a4.622 4.622 0 100 9.244 4.622 4.622 0 000-9.244zm0 7.622a3 3 0 110-6 3 3 0 010 6zm5.804-8.837a1.08 1.08 0 11-2.16 0 1.08 1.08 0 012.16 0z" />
        </svg>
      );
    case 'facebook':
      return (
        <svg viewBox="0 0 24 24" className={className} fill="currentColor" aria-hidden="true">
          <path d="M24 12.073C24 5.405 18.627 0 12 0S0 5.405 0 12.073C0 18.1 4.388 23.094 10.125 24v-8.437H7.078v-3.49h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.49h-2.796V24C19.612 23.094 24 18.1 24 12.073z" />
        </svg>
      );
    case 'linkedin':
      return (
        <svg viewBox="0 0 24 24" className={className} fill="currentColor" aria-hidden="true">
          <path d="M20.447 20.452h-3.554v-5.569c0-1.328-.027-3.037-1.852-3.037-1.853 0-2.136 1.445-2.136 2.939v5.667H9.351V9h3.414v1.561h.046c.477-.9 1.637-1.85 3.37-1.85 3.601 0 4.267 2.37 4.267 5.455v6.286zM5.337 7.433a2.062 2.062 0 01-2.063-2.065 2.064 2.064 0 114.127 0 2.063 2.063 0 01-2.063 2.065zm1.782 13.019H3.555V9h3.564v11.452zM22.225 0H1.771C.792 0 0 .774 0 1.729v20.542C0 23.227.792 24 1.771 24h20.451C23.2 24 24 23.227 24 22.271V1.729C24 .774 23.2 0 22.222 0h.003z" />
        </svg>
      );
    case 'twitter':
      return (
        <svg viewBox="0 0 24 24" className={className} fill="currentColor" aria-hidden="true">
          <path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z" />
        </svg>
      );
    case 'youtube':
      return (
        <svg viewBox="0 0 24 24" className={className} fill="currentColor" aria-hidden="true">
          <path d="M23.498 6.186a3.016 3.016 0 00-2.122-2.136C19.505 3.545 12 3.545 12 3.545s-7.505 0-9.377.505A3.017 3.017 0 00.502 6.186C0 8.07 0 12 0 12s0 3.93.502 5.814a3.016 3.016 0 002.122 2.136c1.871.505 9.376.505 9.376.505s7.505 0 9.377-.505a3.015 3.015 0 002.122-2.136C24 15.93 24 12 24 12s0-3.93-.502-5.814zM9.545 15.568V8.432L15.818 12l-6.273 3.568z" />
        </svg>
      );
    case 'tiktok':
      return (
        <svg viewBox="0 0 24 24" className={className} fill="currentColor" aria-hidden="true">
          <path d="M12.525.02c1.31-.02 2.61-.01 3.91-.02.08 1.53.63 3.09 1.75 4.17 1.12 1.11 2.7 1.62 4.24 1.79v4.03c-1.44-.05-2.89-.35-4.2-.97-.57-.26-1.1-.59-1.62-.93-.01 2.92.01 5.84-.02 8.75-.08 1.4-.54 2.79-1.35 3.94-1.31 1.92-3.58 3.17-5.91 3.21-1.43.08-2.86-.31-4.08-1.03-2.02-1.19-3.44-3.37-3.65-5.71-.02-.5-.03-1-.01-1.49.18-1.9 1.12-3.72 2.58-4.96 1.66-1.44 3.98-2.13 6.15-1.72.02 1.48-.04 2.96-.04 4.44-.99-.32-2.15-.14-3.02.74-.63.58-.94 1.4-.89 2.25.05 1.2.87 2.28 2.02 2.64 1.14.37 2.43.12 3.31-.64.56-.49.93-1.17 1.03-1.9.17-1.36.09-2.73.1-4.09z" />
        </svg>
      );
    case 'whatsapp':
      return (
        <svg viewBox="0 0 24 24" className={className} fill="currentColor" aria-hidden="true">
          <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.435 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z" />
        </svg>
      );
    default:
      return null;
  }
};
